import { callApi } from './api';
import { Schedule, ScheduleQueueItem } from '@/models/schedule';
import { ScheduleCadence, ScheduleQueueItemStatus, ScheduleTargetType } from '@/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const schedulesApiUrl = import.meta.env.VITE_API_URL_V1 + '/schedules';

// ── Schedules ──────────────────────────────────────────────────────────────

export const getSchedules = async () => callApi<Schedule[]>({ url: schedulesApiUrl, method: 'GET' });

export const useSchedules = () => useQuery({ queryKey: ['schedules'], queryFn: getSchedules });

export const getSchedule = async (scheduleId: string) =>
    callApi<Schedule>({ url: `${schedulesApiUrl}/${scheduleId}`, method: 'GET' });

export const useSchedule = (scheduleId: string | undefined) =>
    useQuery({
        queryKey: ['schedule', scheduleId],
        queryFn: () => getSchedule(scheduleId as string),
        enabled: !!scheduleId,
    });

export type CreateSchedulePayload = {
    name: string;
    target: { type: ScheduleTargetType; id: string };
    cadence: ScheduleCadence;
    publishAtHourUtc: number;
    publishAtMinuteUtc: number;
    enabled?: boolean;
};

export const createSchedule = async (payload: CreateSchedulePayload) =>
    callApi<Schedule>({ url: schedulesApiUrl, method: 'POST', data: payload });

export const useCreateSchedule = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: createSchedule,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules'] }),
    });
};

export type UpdateSchedulePayload = Partial<Pick<Schedule, 'name' | 'cadence' | 'publishAtHourUtc' | 'publishAtMinuteUtc' | 'enabled'>>;

export const updateSchedule = async ({ scheduleId, ...patch }: UpdateSchedulePayload & { scheduleId: string }) =>
    callApi<Schedule>({ url: `${schedulesApiUrl}/${scheduleId}`, method: 'PUT', data: patch });

export const useUpdateSchedule = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: updateSchedule,
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: ['schedules'] });
            qc.invalidateQueries({ queryKey: ['schedule', vars.scheduleId] });
        },
    });
};

export const deleteSchedule = async ({ scheduleId }: { scheduleId: string }) =>
    callApi({ url: `${schedulesApiUrl}/${scheduleId}`, method: 'DELETE' });

export const useDeleteSchedule = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: deleteSchedule,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules'] }),
    });
};

// ── Queue items ────────────────────────────────────────────────────────────

export const getQueueItems = async (scheduleId: string, status?: ScheduleQueueItemStatus) => {
    const params = status ? `?status=${encodeURIComponent(status)}` : '';
    return callApi<ScheduleQueueItem[]>({ url: `${schedulesApiUrl}/${scheduleId}/queue${params}`, method: 'GET' });
};

export const useQueueItems = (scheduleId: string | undefined, status?: ScheduleQueueItemStatus) =>
    useQuery({
        queryKey: ['schedule', scheduleId, 'queue', status ?? 'all'],
        queryFn: () => getQueueItems(scheduleId as string, status),
        enabled: !!scheduleId,
    });

export type CreateQueueItemPayload = { scheduleId: string; payload: Record<string, unknown>; orderIndex?: number };

export const createQueueItem = async ({ scheduleId, ...body }: CreateQueueItemPayload) =>
    callApi<ScheduleQueueItem>({ url: `${schedulesApiUrl}/${scheduleId}/queue`, method: 'POST', data: body });

export const useCreateQueueItem = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: createQueueItem,
        onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['schedule', vars.scheduleId, 'queue'] }),
    });
};

export type UpdateQueueItemPayload = { scheduleId: string; itemId: string; payload?: Record<string, unknown>; orderIndex?: number };

export const updateQueueItem = async ({ scheduleId, itemId, ...body }: UpdateQueueItemPayload) =>
    callApi<ScheduleQueueItem>({ url: `${schedulesApiUrl}/${scheduleId}/queue/${itemId}`, method: 'PUT', data: body });

export const useUpdateQueueItem = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: updateQueueItem,
        onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['schedule', vars.scheduleId, 'queue'] }),
    });
};

export const deleteQueueItem = async ({ scheduleId, itemId }: { scheduleId: string; itemId: string }) =>
    callApi({ url: `${schedulesApiUrl}/${scheduleId}/queue/${itemId}`, method: 'DELETE' });

export const useDeleteQueueItem = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: deleteQueueItem,
        onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['schedule', vars.scheduleId, 'queue'] }),
    });
};

export const reorderQueue = async ({ scheduleId, updates }: { scheduleId: string; updates: { itemId: string; orderIndex: number }[] }) =>
    callApi({ url: `${schedulesApiUrl}/${scheduleId}/queue/reorder`, method: 'POST', data: { updates } });

export const useReorderQueue = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: reorderQueue,
        onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['schedule', vars.scheduleId, 'queue'] }),
    });
};

export const publishNow = async ({ scheduleId }: { scheduleId: string }) =>
    callApi<{ schedule: Schedule; item: ScheduleQueueItem }>({
        url: `${schedulesApiUrl}/${scheduleId}/publish-now`,
        method: 'POST',
    });

export const usePublishNow = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: publishNow,
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: ['schedules'] });
            qc.invalidateQueries({ queryKey: ['schedule', vars.scheduleId] });
            qc.invalidateQueries({ queryKey: ['schedule', vars.scheduleId, 'queue'] });
        },
    });
};
